<script lang="ts" setup>
import type { Group } from '~/models/Group'
import { useI18n } from 'vue-i18n'
import { ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { faArrowLeft } from '@fortawesome/duotone-regular-svg-icons'
import useGroupsStore from '~/stores/groups'
import Button from '~/components/ui/Button.vue'
import AuDrawer from '~/components/ui/AuDrawer.vue'
import AuCallout from '~/components/ui/AuCallout.vue'
import SettingsGroupView from '~/components/settings/SettingsGroups/SettingsGroupView.vue'
import SettingsGroupList from '~/components/settings/SettingsGroups/SettingsGroupList.vue'

const props = defineProps<{
  open: boolean
}>()

const emit = defineEmits(['back', 'close'])

const { t } = useI18n()
const { loadGroups } = useGroupsStore()
const { hasGroups, isLoading } = storeToRefs(useGroupsStore())

const openGroup = ref<Nullable<Group>>(null)

const setOpenGroup = (group: Group) => {
  openGroup.value = group
}

function goBack() {
  if (openGroup.value) {
    openGroup.value = null
    return
  }
  emit('back')
}

watch(props, (value) => {
  if (value.open) {
    openGroup.value = null
    loadGroups()
  }
})
</script>

<template>
  <AuDrawer
    :backdrop="false"
    :open="props.open"
    :title="t('settings.drawers.groups')"
    center-title
    @close="emit('close')"
  >
    <template #content>
      <Button
        :aria-label="t('back')"
        class="absolute top-6 left-6"
        shape="square"
        size="xl"
        @click="goBack"
      >
        <font-awesome-icon :icon="faArrowLeft" />
      </Button>
      <div
        v-if="!hasGroups && !isLoading"
        class="flex flex-col gap-6 py-6"
      >
        <AuCallout
          :title="t('groups.noGroupsTitle')"
          variant="warning"
        >
          {{ t('groups.noGroups') }}
        </AuCallout>
      </div>
      <SettingsGroupView
        v-if="openGroup"
        :group="openGroup"
        @back="goBack"
        @delete-group="goBack"
        @group-created="setOpenGroup"
      />
      <SettingsGroupList
        v-else-if="!openGroup && !isLoading"
        @open-group="setOpenGroup"
      />
    </template>
  </AuDrawer>
</template>
